import type { CInvoice, CPayout, TalentSelf } from "./data";
import { profileGaps, type CreatorRules } from "./model";
import { tr, type Lang } from "./lang";
import { creatorGuideText, type CreatorGuide } from "../agentur/coco-rundgang-texte";

/**
 * Einstieg für neue Creator (Befund 7, Umfang wie im bisherigen Portal): Checkliste „Erste Schritte“ und kurze
 * Einführung je Seite. Rein und ohne Datenbank, damit Tests die Regeln prüfen.
 */

export type FirstStepKey = "profile" | "tax" | "bank" | "signature" | "invoice";

export interface FirstStep {
  key: FirstStepKey;
  done: boolean;
  label: string;
  hint: string;
  action: string;
  to: string;
}

const BANK_FIELDS = new Set(["iban", "accountHolder", "bankName", "bic"]);

/** Steuerstatus angegeben: Kleinunternehmer oder Steuernummer bzw. USt-IdNr. (small_business = false ist nur die Vorgabe). */
export function taxStatusGiven(tl: Pick<TalentSelf, "small_business" | "tax_number" | "vat_id">): boolean {
  const filled = (v: string | null | undefined) => !!v && v.trim() !== "";
  return tl.small_business || filled(tl.tax_number) || filled(tl.vat_id);
}

/**
 * Profil (Name und Anschrift), Steuerstatus, Bankverbindung, Unterschrift, erste Rechnung. Bei vereinbartem
 * Gutschriftverfahren stellt die Agentur die Rechnungen aus; der letzte Schritt entfällt dann.
 */
export function firstSteps(input: {
  talent: TalentSelf;
  payout: CPayout | null;
  hasSignature: boolean;
  invoices: readonly Pick<CInvoice, "status">[];
  rules: CreatorRules;
  lang?: Lang;
}): FirstStep[] {
  const lang = input.lang ?? "de";
  const t = (de: string, en: string) => tr(lang, de, en);
  const tl = input.talent;
  const filled = (v: string | null | undefined) => !!v && v.trim() !== "";
  const gaps = profileGaps(tl, input.payout, input.rules);
  const steps: FirstStep[] = [
    {
      key: "profile",
      done: filled(tl.first_name) && filled(tl.last_name) && filled(tl.street) && filled(tl.postal_code) && filled(tl.city) && filled(tl.country),
      label: t("Name und Anschrift eintragen", "Enter your name and address"),
      hint: t("Sie stehen auf jeder deiner Rechnungen.", "They appear on each of your invoices."),
      action: t("Profil öffnen", "Open profile"),
      to: "/creator/profil#adresse",
    },
    {
      key: "tax",
      done: taxStatusGiven(tl),
      label: t("Steuerstatus angeben", "Enter your tax status"),
      hint: t("Kleinunternehmer oder Steuernummer bzw. USt-IdNr.: daran hängt, ob deine Rechnung Umsatzsteuer ausweist.", "Small business or tax number or VAT ID: this decides whether your invoice shows VAT."),
      action: t("Steuerstatus wählen", "Choose tax status"),
      to: "/creator/profil#steuerstatus",
    },
    {
      key: "bank",
      done: !gaps.blocking.some((f) => BANK_FIELDS.has(f)),
      label: t("Bankverbindung hinterlegen", "Add your bank details"),
      hint: t("Dorthin zahlt deine Agentur dein Geld aus.", "Your agency pays your money there."),
      action: t("Bankverbindung eintragen", "Enter bank details"),
      to: "/creator/profil#auszahlung",
    },
    {
      key: "signature",
      done: input.hasSignature,
      label: t("Unterschrift speichern", "Save your signature"),
      hint: t("Sie füllt Verträge vor, du unterschreibst dann mit einem Tipp.", "It pre-fills contracts, then you sign with one tap."),
      action: t("Unterschrift zeichnen", "Draw signature"),
      to: "/creator/profil#unterschrift",
    },
  ];
  if (!input.talent.self_billing_agreed_on) {
    steps.push({
      key: "invoice",
      done: input.invoices.some((i) => !["draft", "discarded", "withdrawn"].includes(i.status)),
      label: t("Erste Rechnung einreichen", "Submit your first invoice"),
      hint: t("Der Assistent führt dich Schritt für Schritt bis zum fertigen PDF.", "The assistant guides you step by step to the finished PDF."),
      action: t("Rechnung einreichen", "Submit invoice"),
      to: "/creator/rechnungen/einreichen",
    });
  }
  return steps;
}

// ── Einführung je Seite ─────────────────────────────────────────────────────

export type GuideKey = CreatorGuide;

/** Welche Einführung gehört zu diesem Weg? */
export function guideFor(pathname: string): GuideKey | null {
  const p = pathname.replace(/\/+$/, "");
  if (p === "/creator") return "overview";
  if (p === "/creator/rechnungen/einreichen") return "wizard";
  const map: [string, GuideKey][] = [
    ["/creator/deals", "deals"], ["/creator/rechnungen", "invoices"], ["/creator/vertraege", "contracts"], ["/creator/nachrichten", "chat"],
    ["/creator/kalender", "calendar"], ["/creator/profil", "profile"], ["/creator/benachrichtigungen", "bell"],
  ];
  for (const [base, key] of map) if (p === base || p.startsWith(base + "/")) return key;
  return null;
}

export interface GuideText {
  title: string;
  text: string;
  bullets: string[];
}

/** Texte stehen mit allen Coco-Texten in pages/agentur/coco-rundgang-texte.ts (Rundgang, 20.10.2026) */
export function guideText(key: GuideKey, lang: Lang, agency: string): GuideText {
  return creatorGuideText(key, (de, en) => tr(lang, de, en), agency);
}

